import React from 'react';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import {IElementLikertScale, ILikertScaleQuestion, ILikertScaleScale} from '../../../../../incase-domain/elements/question/likertScale';
import {LikertScaleInteractions} from '../../../../../incase-domain/proptypes/runfile';
import DebouncedLikertScaleSlider from '../../../../../incase-domain/components/slider/DebouncedLikertScaleSlider';

interface ILikertScaleQuestionsProps {
    device: DeviceType;
    element: IElementLikertScale;
    elementInteractions: LikertScaleInteractions;
    savePresentationRunFile: (elementRunFile: Record<string, unknown>, newPoints?: number) => void;
}

function LikertScaleQuestions(props: ILikertScaleQuestionsProps): JSX.Element {
    const {device, element, elementInteractions, savePresentationRunFile} = props;

    const handleClick = (question: ILikertScaleQuestion, scale: ILikertScaleScale): void => {
        let updatedAnswers;
        const answerExist = elementInteractions.likertScaleAnswerClicked?.find((answer) => answer.question.id === question.id);
        if (answerExist) {
            updatedAnswers = elementInteractions.likertScaleAnswerClicked?.map((answer) =>
                answer.question.id === question.id ? {...answer, scale} : answer
            );
        } else {
            updatedAnswers = [...(elementInteractions.likertScaleAnswerClicked || []), {question, scale}];
        }
        savePresentationRunFile({
            ...elementInteractions,
            likertScaleAnswerClicked: updatedAnswers,
            likertScaleAnswer: updatedAnswers,
        });
    };

    const getIndexOfScale = (questionId: string): number | undefined => {
        const scaleId = elementInteractions.likertScaleAnswerClicked?.find((answer) => answer.question.id === questionId)?.scale.id;
        return scaleId ? element.scale.map((scale) => scale.id).indexOf(scaleId) : undefined;
    };

    if (element.sliderView) {
        return (
            <div className="c-likertScale">
                {element.questions.map((question) => (
                    <div
                        style={{
                            fontSize: `${element.fontSize || 'initial'}em`,
                            fontFamily: element.fontFamily?.name,
                        }}
                        key={question.id}
                        className="c-likertScale__sliderQuestion"
                    >
                        {question.text}
                        <div className="c-likertScale__sliderQuestion_slider">
                            <DebouncedLikertScaleSlider
                                min={0}
                                max={element.scale.length - 1}
                                step={1}
                                value={getIndexOfScale(question.id)}
                                marks={element.scale.reduce((acc, scale, index) => ({...acc, [index]: scale.text}), {})}
                                onChange={(v) => {
                                    Number.isInteger(v) && handleClick(question, element.scale[v as number]);
                                }}
                                disabled={elementInteractions.answered}
                                variant="likertScale"
                            />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (device === DeviceType.MOBILE) {
        return (
            <div className="c-likertScale c-likertScale--mobile">
                {element.questions.map((question) => (
                    <div key={question.id} className="u-mt-2">
                        <div
                            style={{
                                fontFamily: element.fontFamily?.name,
                            }}
                            className="c-likertScale__question"
                        >
                            {question.text}
                        </div>
                        <div className="c-likertScale__scale u-mt-1">
                            {element.scale.map((scale) => (
                                <div key={scale.id} className="c-likertScale__scale_scaleRow">
                                    <span
                                        style={{
                                            fontFamily: element.fontFamily?.name,
                                        }}
                                        onClick={() => handleClick(question, scale)}
                                    >
                                        {scale.text}
                                    </span>
                                    <input
                                        type="radio"
                                        name={`${question.id}`}
                                        key={`${question.id}-${scale.id}`}
                                        onClick={() => handleClick(question, scale)}
                                        disabled={elementInteractions.answered}
                                        defaultChecked={
                                            elementInteractions.likertScaleAnswerClicked?.find(
                                                (answer) => answer.question.id === question.id
                                            )?.scale.id === scale.id
                                        }
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="c-likertScale c-likertScale__gridContainer">
            {element.scale.map((scale, index) => (
                <div
                    key={scale.id}
                    style={{
                        gridRow: 1,
                        gridColumn: index + 2,
                        fontSize: `${element.fontSize || 'initial'}em`,
                        fontFamily: element.fontFamily?.name,
                    }}
                    className="c-likertScale__scaleText"
                >
                    {scale.text}
                </div>
            ))}

            {element.questions.map((question, index) => (
                <div
                    key={question.id}
                    style={{
                        gridRow: index + 2,
                        gridColumn: 1,
                        fontSize: `${element.fontSize || 'initial'}em`,
                        fontFamily: element.fontFamily?.name,
                    }}
                    className="c-likertScale__question u-mt-1"
                >
                    {question.text}
                </div>
            ))}

            {element.questions.map((question, i) =>
                element.scale.map((scale, j) => (
                    <input
                        type="radio"
                        name={`${question.id}`}
                        style={{gridRow: i + 2, gridColumn: j + 2}}
                        key={question.id + scale.id}
                        onClick={() => handleClick(question, scale)}
                        disabled={elementInteractions.answered}
                        defaultChecked={
                            elementInteractions.likertScaleAnswerClicked?.find((answer) => answer.question.id === question.id)?.scale.id ===
                            scale.id
                        }
                    />
                ))
            )}
        </div>
    );
}

export default LikertScaleQuestions;
